import React, { useContext, useEffect, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import CloseIcon from '@mui/icons-material/Close';
import type { Theme } from '@mui/material';
import { Button, IconButton, Snackbar, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';

import type { GetAnalysisResultsReturn } from '../../../@types/contracts';
import { ImageRerunStatus } from '../../../@types/contracts';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'UserRole' is defined but never used.
import { UserRole } from '../../../@types/global';
import { useLocalStorage } from '../../../utils/hooks/useLocalStorage';
import { invalidateRerunQueries } from '../../../utils/hooks/useRerunAi';
import { colors } from '../../../utils/theme';
import { CONNECTED } from '../../login/login.constants';
import AnnotatorConstants from '../annotator.constants';
import { rerunAiAnalysis } from '../api';
import { showPastVisitsAtom } from '../atoms';

// eslint-disable-next-line import/no-cycle -- Dependency cycle detected.
import RerunErrorDialog from './RerunErrorDialog';
import type {
  ImageToRerun,
  RotationContextProps,
  RotationContextTypes,
} from './RotationContext.types';
import {
  populateCurrentlyRotatingImages,
  trackRotateButtonClick,
  useTrackRerunAnalysisClick,
} from './utils';

const localStyles = makeStyles((theme: Theme) => ({
  aiRerunSuccessMessage: {
    background: colors.PRIMARY,
    color: 'white',
    marginRight: 300,
    width: 650,
  },
  aiErrorMessage: {
    background: colors.ORANGE,
    color: 'black',
    width: 400,
    marginRight: 300,
    lineHeight: 1,
  },
  rerunAiSnackbarButton: {
    marginRight: 50,
    paddingTop: 1,
    paddingBottom: 1,
    color: 'white',
    borderColor: 'white',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'white',
      color: colors.PRIMARY,
    },
  },
  rerunAiText: {
    fontSize: 13,
    marginRight: theme.spacing(2),
  },
}));

const RotationContext = React.createContext({} as RotationContextTypes);

/**
 * TODO: Too many states tracking image rotation, this means we have to synchronize
 * many pieces of state. Consolidate rotation state into just one piece of state
 */
export function RotationProvider({
  children,
  selectedPatientId,
}: RotationContextProps) {
  const [isRotationButtonHovered, setIsRotationButtonHovered] = useState([]);
  const [currentlyRotatingImages, setCurrentlyRotatingImages] = useState({
    images: [],
    imageIds: [],
  });
  const [isLoadingRerun, setIsLoadingRerun] = useState(false);
  const [snackbarState, setSnackbarState] = React.useState({
    open: false,
    message: '',
    state: 'INFO',
  });
  const classes = localStyles();
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);

  const userAccountKey = `${user.primaryOrganizationId}/${
    user.primaryPracticeId
  }/${user.id}/${selectedPatientId ?? ''}`;

  const [imageRotationStore, setImageRotationStore] = useLocalStorage(
    AnnotatorConstants.IMAGE_ROTATIONS_LOCAL_STORAGE_KEY,
    {
      [userAccountKey]: {},
    },
  );
  const [rotatedImages, setRotatedImages] = useState({});
  const [mirroredImages, setMirroredImages] = useState<Record<string, boolean>>(
    {},
  );
  const dispatch = useDispatch();
  const patients = useSelector(
    (state: RootStateOrAny) => state?.annotator?.patients,
  );
  const [shouldPurgeImageCache, setShouldPurgeImageCache] = useState(false);
  const [rerunPopupMargin, setRerunPopupMargin] = useState(null);
  const [errorImageIds, setErrorImageIds] = useState<string[]>([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isOnSingleImageView, setIsOnSingleImageView] = useState(false);
  const videaConnectorStatus = useSelector(
    (state: RootStateOrAny) => state?.login?.beacon?.status,
  );

  const queryClient = useQueryClient();
  const { trackRerunAnalysisClick } = useTrackRerunAnalysisClick();
  const { t } = useLingui();

  const isConnectedToConnector = videaConnectorStatus === CONNECTED;

  const handleClose = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState((p) => ({
      ...p,
      open: false,
    }));
  };

  const closeSnackbarErrorOnSingleImage = () => {
    if (snackbarState.state === 'ERROR' && snackbarState.open) {
      setSnackbarState((prev) => ({
        ...prev,
        open: false,
      }));
    }
  };

  const resetImageRotationState = () => {
    setImageRotationStore({
      ...imageRotationStore[userAccountKey],
      [userAccountKey]: {},
    });
    setCurrentlyRotatingImages({
      imageIds: [],
      images: [],
    });
    setRotatedImages({});
  };

  useEffect(() => {
    if (imageRotationStore[userAccountKey]) {
      setRotatedImages({
        ...imageRotationStore[userAccountKey],
      });
      setCurrentlyRotatingImages(
        // @ts-expect-error TS(2345): Argument of type '{ images: { rotation: {}; imageI... Remove this comment to see the full error message
        populateCurrentlyRotatingImages(imageRotationStore[userAccountKey]),
      );
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'imageRotationStore' and 'userAccountKey'. Either include them or remove the dependency array. */
  }, []);

  const persistImageRotationLocally = (imageId: string) => {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const currentRotation = imageRotationStore[userAccountKey]?.[imageId] ?? 0;
    const newRotation = currentRotation >= 270 ? 0 : currentRotation + 90;
    setErrorImageIds([]);
    const accountKeyValue = {
      ...imageRotationStore[userAccountKey],
      [imageId]: newRotation,
    };
    setImageRotationStore({
      ...imageRotationStore,
      [userAccountKey]: accountKeyValue,
    });
    setRotatedImages({
      ...accountKeyValue,
    });
    setCurrentlyRotatingImages(
      // @ts-expect-error TS(2345): Argument of type '{ images: { rotation: {}; imageI... Remove this comment to see the full error message
      populateCurrentlyRotatingImages(accountKeyValue),
    );
  };

  const rotateImage = (image: any) => {
    if (!image) {
      return;
    }
    persistImageRotationLocally(image.objectId || image.imageId);

    trackRotateButtonClick({
      user,
      patientId: selectedPatientId,
      objectId: image.objectId || image.imageId,
    });
  };

  const resetImageRotation = (imageId: string) => {
    const newRotation = 0;
    setErrorImageIds([]);
    const accountKeyValue = {
      ...imageRotationStore[userAccountKey],
      [imageId]: newRotation,
    };
    setImageRotationStore({
      ...imageRotationStore,
      [userAccountKey]: accountKeyValue,
    });
    setRotatedImages({
      ...accountKeyValue,
    });
    setCurrentlyRotatingImages(
      // @ts-expect-error TS(2345): Argument of type '{ images: { rotation: {}; imageI... Remove this comment to see the full error message
      populateCurrentlyRotatingImages(accountKeyValue),
    );
  };

  const toggleImageMirror = (imageId: string) => {
    setMirroredImages((prev) => ({
      ...prev,
      [imageId]: !prev[imageId],
    }));
  };

  const resetImageMirror = (imageId: string) => {
    setMirroredImages((prev) => {
      const newState = { ...prev };
      delete newState[imageId];
      return newState;
    });
  };

  useEffect(() => {
    if (currentlyRotatingImages.imageIds.length > 0) {
      setSnackbarState({
        message: '',
        open: true,
        state: 'INFO',
      });
    } else {
      setSnackbarState((p) => ({
        ...p,
        open: false,
      }));
    }
  }, [currentlyRotatingImages.imageIds.length]);

  const saveAndRerunVisit = async (
    imagesToRerun: ImageToRerun[],
    estimatedVisitId: number,
  ) => {
    trackRerunInvocation(selectedPatientId, imagesToRerun, 'compare_visits');

    try {
      const rerunAiAnalysisResults = await rerunAiAnalysis(
        imagesToRerun,
        estimatedVisitId,
      );
      checkForAndNotifyOfRerunFailure(rerunAiAnalysisResults);
      await invalidateRerunQueries(queryClient);
      resetImageRotationState();
      setImageRotationStore({
        ...imageRotationStore,
        [userAccountKey]: {},
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoadingRerun(false);
    }
  };

  const checkForAndNotifyOfRerunFailure = (
    rerunAiAnalysisResults: GetAnalysisResultsReturn[],
  ) => {
    const imagesThatFailedRerun = rerunAiAnalysisResults.filter(
      (result) => result.rerunStatus !== ImageRerunStatus.SUCCESS,
    );

    setErrorImageIds(imagesThatFailedRerun.map((img) => img.imageId));

    if (imagesThatFailedRerun.length) {
      setSnackbarState({
        open: true,
        message: 'Some images failed to load the updated analysis.',
        state: 'ERROR',
      });
    }
  };

  const trackRerunInvocation = (
    patientId: string,
    imagesToReanalyze: ImageToRerun[],
    rerunSource: string,
  ) => {
    trackRerunAnalysisClick({
      patientId,
      numberOfImages: imagesToReanalyze.length,
      rerunSource,
    });

    setIsLoadingRerun(true);
    setShowErrorDialog(false);
    setShouldPurgeImageCache(true);
  };

  const saveAndRerunAi = async (currentImage?: any) => {
    // Always trigger rerun if the button is visible (either rotated or mirrored images)
    const hasRotatedImages = currentlyRotatingImages.images.length > 0;
    const hasMirroredImages =
      currentImage &&
      mirroredImages[currentImage.objectId || currentImage.imageId];

    if (hasRotatedImages || hasMirroredImages) {
      trackRerunInvocation(
        selectedPatientId,
        currentlyRotatingImages.images,
        hasRotatedImages ? 'rotation_rerun' : 'mirror_rerun',
      );

      try {
        // Build rerun request for all modified images
        let rerunAnalysesProps: Array<{
          imageId: string;
          rotation?: number;
          mirrorImage?: boolean;
        }> = [];

        // Add rotated images to the rerun request
        if (hasRotatedImages) {
          rerunAnalysesProps = currentlyRotatingImages.images.map((img) => ({
            // @ts-expect-error TS(2339): Property 'imageId' does not exist on type 'never'.
            imageId: img.imageId,
            // Multiplying by -1 since the rotation in the backend happens in the opposite direction of the UI
            // @ts-expect-error TS(2339): Property 'rotation' does not exist on type 'never'... Remove this comment to see the full error message
            rotation: img.rotation * -1,
            // Check if the current image is also mirrored
            mirrorImage:
              hasMirroredImages &&
              currentImage &&
              // @ts-expect-error TS(2339): Property 'imageId' does not exist on type 'never'... Remove this comment to see the full error message
              (img.imageId === currentImage.imageId ||
                // @ts-expect-error TS(2339): Property 'imageId' does not exist on type 'never'... Remove this comment to see the full error message
                img.imageId === currentImage.objectId),
          }));
        }

        // If there are no rotated images but the current image is mirrored, add it to the rerun request
        if (!hasRotatedImages && hasMirroredImages && currentImage) {
          const imageId = currentImage.imageId || currentImage.objectId;
          rerunAnalysesProps = [
            {
              imageId,
              rotation: undefined,
              mirrorImage: true,
            },
          ];
        }

        const rerunAiAnalysisResults =
          await rerunAiAnalysis(rerunAnalysesProps);

        // Reset states after successful rerun
        if (hasRotatedImages) {
          resetImageRotationState();
        }
        if (hasMirroredImages && currentImage) {
          // Reset the frontend mirror state after rerun
          resetImageMirror(currentImage.objectId || currentImage.imageId);
        }
        dispatch({
          type: AnnotatorConstants.RESET_IMAGES_FOR_PATIENT,
          response: {
            data: { patientId: selectedPatientId },
          },
        });
        const patient = patients[selectedPatientId];
        if (patient) {
          await invalidateRerunQueries(queryClient);
        }
        setImageRotationStore({
          ...imageRotationStore,
          [userAccountKey]: {},
        });

        checkForAndNotifyOfRerunFailure(rerunAiAnalysisResults);
      } catch (err) {
        console.error(err);
        setShowErrorDialog(true);
      } finally {
        setIsLoadingRerun(false);
      }
    } else {
      setIsLoadingRerun(false);
    }
  };

  const value: RotationContextTypes = {
    isRotationButtonHovered,
    // @ts-expect-error TS(2322): Type 'Dispatch<SetStateAction<never[]>>' is not as... Remove this comment to see the full error message
    setIsRotationButtonHovered,
    currentlyRotatingImages,
    // @ts-expect-error TS(2322): Type 'Dispatch<SetStateAction<{ images: never[]; i... Remove this comment to see the full error message
    setCurrentlyRotatingImages,
    rotateImage,
    saveAndRerunAi,
    saveAndRerunVisit,
    isLoadingRerun,
    rotatedImages,
    shouldPurgeImageCache,
    setShouldPurgeImageCache,
    snackbarState,
    setRerunPopupMargin,
    errorImageIds,
    resetImageRotationState,
    closeSnackbarErrorOnSingleImage,
    setIsOnSingleImageView,
    isConnectedToConnector,
    resetImageRotation,
    mirroredImages,
    toggleImageMirror,
    resetImageMirror,
  };

  const showPastVisits = useAtomValue(showPastVisitsAtom);

  return (
    <RotationContext.Provider value={value}>
      <RerunErrorDialog open={showErrorDialog} setOpen={setShowErrorDialog} />
      {!isOnSingleImageView && !isConnectedToConnector && !showPastVisits && (
        <Snackbar
          open={snackbarState.open && !isLoadingRerun && !showErrorDialog}
          // @ts-expect-error TS(2322): Type '(event: React.SyntheticEvent | React.MouseEv... Remove this comment to see the full error message
          onClose={handleClose}
          message={snackbarState.message}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'MarginBotto... Remove this comment to see the full error message
          style={{ marginBottom: rerunPopupMargin }}
          action={
            <>
              {!(snackbarState.state === 'ERROR') && (
                <>
                  <Typography className={classes.rerunAiText}>
                    {`${currentlyRotatingImages.imageIds.length} ${t`image(s) were rotated. Please rerun the AI for accurate analysis.`}`}
                  </Typography>
                  <Button
                    variant="outlined"
                    className={classes.rerunAiSnackbarButton}
                    onClick={async () => {
                      await saveAndRerunAi();
                    }}
                    disabled={isLoadingRerun}
                  >
                    Rerun AI
                  </Button>
                </>
              )}

              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
          ContentProps={{
            classes: {
              root:
                snackbarState.state === 'INFO'
                  ? classes.aiRerunSuccessMessage
                  : classes.aiErrorMessage,
            },
          }}
        />
      )}

      {children}
    </RotationContext.Provider>
  );
}

export const useRotation = () => useContext(RotationContext);
